import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";
import { Navbar } from "../components/Navbar";
import { Banner } from "../components/Banner";
import { AppDownloadNotice } from "../components/AppDownloadNotice";
import { RoomCard } from "../components/RoomCard";
import { ChevronLeft, ChevronRight, Plus, Play, Users, Radio, Eye } from "lucide-react";
import { UserAvatar } from "../components/UserAvatar";

function Row({ title, rooms, testid }) {
  const ref = useRef();
  if (!rooms.length) return null;
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 600, behavior: "smooth" });
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="flex items-center justify-between px-4 sm:px-8 mb-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white">{title}</h2>
        <div className="hidden sm:flex gap-2">
          <button onClick={() => scroll(-1)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white transition"><ChevronLeft size={18} /></button>
          <button onClick={() => scroll(1)} className="p-1.5 rounded-full bg-white/5 hover:bg-white/15 text-white transition"><ChevronRight size={18} /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto no-scrollbar px-4 sm:px-8 pb-4">
        {rooms.map((r) => <RoomCard key={r.id} room={r} />)}
      </div>
    </section>
  );
}

export default function Home() {
  const { user } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [lives, setLives] = useState([]);

  useEffect(() => {
    api.get("/rooms").then(({ data }) => setRooms(data)).catch(() => {});
    api.get("/live").then(({ data }) => setLives(data)).catch(() => {});
  }, []);

  const live = rooms.filter((r) => r.participant_count > 0);
  const popular = [...rooms].sort((a, b) => b.participant_count - a.participant_count).filter((r) => r.participant_count > 0);
  const featured = live[0] || rooms[0];

  return (
    <div className="min-h-screen bg-[#141414]">
      <AppDownloadNotice />
      <Banner />
      <Navbar />

      {/* Hero */}
      <div className="relative h-[62vh] min-h-[440px] w-full overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1485095329183-d0797cdc5676?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt="hero" className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #141414 6%, transparent 60%), linear-gradient(to right, rgba(20,20,20,0.9) 0%, transparent 60%)" }} />
        <div className="relative z-10 flex flex-col justify-end h-full max-w-[1600px] mx-auto px-4 sm:px-8 pb-16">
          <span className="inline-flex items-center gap-2 w-fit rounded-full bg-[#E50914] px-3 py-1 text-xs font-bold uppercase text-white mb-4">
            <Play size={12} className="fill-white" /> Birlikte İzle
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white max-w-2xl leading-none text-glow-red">
            {settings.site_name}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-300 max-w-xl">{settings.site_description}</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <button data-testid="hero-create-button" onClick={() => navigate(user ? "/create" : "/login")}
              className="flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white font-semibold px-7 py-3 rounded-md backdrop-blur-sm transition-all active:scale-95">
              <Plus size={20} /> Oda Oluştur
            </button>
          </div>
        </div>
      </div>

      <div className="relative z-20 -mt-10">
        {lives.length > 0 && (
          <section className="mb-10" data-testid="row-live-streams">
            <div className="flex items-center gap-2 px-4 sm:px-8 mb-3">
              <span className="w-2.5 h-2.5 rounded-full bg-[#E50914] animate-pulse" />
              <h2 className="text-xl sm:text-2xl font-bold text-white">Canlı Yayınlar</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto no-scrollbar px-4 sm:px-8 pb-4">
              {lives.map((s) => (
                <button key={s.id} data-testid={`live-card-${s.id}`} onClick={() => navigate(`/live/${s.id}`)}
                  className="group relative shrink-0 w-64 rounded-lg overflow-hidden bg-[#181818] border border-white/10 text-left hover:border-[#E50914]/60 transition">
                  <div className="relative h-36 bg-gradient-to-br from-[#2a0505] to-[#141414] flex items-center justify-center overflow-hidden">
                    {s.image_url ? <img src={mediaUrl(s.image_url)} alt={s.title} className="absolute inset-0 w-full h-full object-cover" /> : <UserAvatar user={s.broadcaster} className="h-16 w-16" />}
                    <span className="absolute top-2 left-2 bg-[#E50914] text-white text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />CANLI</span>
                    <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><Radio size={10} /> {s.category}</span>
                  </div>
                  <div className="p-3">
                    <div className="text-white text-sm font-semibold truncate">{s.title}</div>
                    <div className="text-xs text-gray-400 truncate">@{s.broadcaster?.username}</div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
        <Row title="🔴 Canlı Yayınlanan Odalar" rooms={live} testid="row-live" />
        <Row title="En Popüler Odalar" rooms={popular} testid="row-popular" />
        <Row title="En Yeni Odalar" rooms={rooms} testid="row-newest" />

        {rooms.length === 0 && (
          <div className="text-center py-24 px-4">
            <Users size={48} className="mx-auto text-gray-700 mb-4" />
            <h3 className="text-2xl font-bold text-white">Henüz oda yok</h3>
            <p className="text-gray-400 mt-2">İlk odayı sen oluştur ve arkadaşlarını davet et!</p>
            <button data-testid="empty-create-button" onClick={() => navigate(user ? "/create" : "/login")}
              className="mt-6 inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F40612] text-white font-semibold px-6 py-3 rounded-md transition-all active:scale-95">
              <Plus size={20} /> Oda Oluştur
            </button>
          </div>
        )}
      </div>

      <footer className="border-t border-white/5 py-8 text-center text-sm text-gray-600">
        © {new Date().getFullYear()} {settings.site_name} — Tüm hakları saklıdır.
      </footer>
    </div>
  );
}
